import { dateTimeDiff, unix } from '../../utils/datetime';
import { isEmpty } from '../../utils/objects';
import { getHasMetadataOnBackend, setHasMetadataOnBackend } from '../ppt';
import request from '../request';
import { getMetadataDictionaryKey } from '../shared/helpers';
import {
  METADATA_LAST_FETCHED_TIME_LOCAL_STORAGE_KEY,
  METADATA_LOCAL_STORAGE_KEY,
} from '../shared/metadata/constants';
import { Metadata } from '../shared/types';
import { LocalStorageAPI } from './api-types';
import { transformGetLocalDataResponse, transformSaveLocalDataPayload } from './helpers';
import urls from './urls';

export async function saveLocalData(
  payload: LocalStorageAPI.SaveLocalDataRequest
): Promise<LocalStorageAPI.SaveLocalDataResponse> {
  const { presentationId, slideId } = payload;

  const metadataDictionary: Record<string, Metadata> = JSON.parse(
    localStorage.getItem(METADATA_LOCAL_STORAGE_KEY) ?? '{}'
  );

  const metadataKey = getMetadataDictionaryKey(presentationId, slideId);
  const slideMetadata = metadataDictionary[metadataKey];

  const transformedPayload = transformSaveLocalDataPayload(presentationId, slideId, slideMetadata);
  if (!transformedPayload) return { message: 'failed' };

  const { data } = await request.post<LocalStorageAPI.SaveLocalDataResponse>(
    urls.saveLocalData,
    transformedPayload
  );
  return data;
}

export async function getLocalData(
  payload: LocalStorageAPI.GetLocalDataRequest,
  token?: string
): Promise<LocalStorageAPI.GetLocalDataTransformedResponse> {
  const hasMetadataOnBackend = await getHasMetadataOnBackend();
  if (!hasMetadataOnBackend) return;

  const { data } = await request.get<LocalStorageAPI.GetLocalDataResponse>(
    urls.getLocalData(payload.presentationId),
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
  );

  if (isEmpty(data)) {
    setHasMetadataOnBackend(false);
  }

  const transformedResponse = transformGetLocalDataResponse(data);

  localStorage.setItem(METADATA_LOCAL_STORAGE_KEY, JSON.stringify(transformedResponse));
  localStorage.setItem(METADATA_LAST_FETCHED_TIME_LOCAL_STORAGE_KEY, JSON.stringify(unix()));
}

/**
 * checks if last call and current call are more than 10 minutes apart
 * if differece is more than 10 minutes, then returns true, otherwise false
 * Note: Edge case - if local storage does not contain last fetched time, then returns true
 */
export function getCanFetchLocalData() {
  const lastFetchedTime = localStorage.getItem(METADATA_LAST_FETCHED_TIME_LOCAL_STORAGE_KEY);
  if (!lastFetchedTime) return true;

  const parsedLastFetchedTime: UnixTime = JSON.parse(lastFetchedTime);

  const diff = dateTimeDiff(unix(), parsedLastFetchedTime, 'minutes');
  return diff > 10;
}
